import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './StationScene.css';
import CharacterPanel from './CharacterPanel';
import Voice from './Voice';
import { startSceneTeacherSession, startSceneVoiceSession } from './api';
import {
  HAUPTBAHNHOF_BOARD,
  HAUPTBAHNHOF_COUNTER,
  HAUPTBAHNHOF_KIOSK,
  HAUPTBAHNHOF_PLATFORM,
  HAUPTBAHNHOF_TRAVEL_CENTRE,
} from './scenes';
import { euros } from './text';
import WayOn from './WayOn';

/** The role the migration casts Katrin in, and the one the voice session is opened against. */
const CLERK = 'clerk';

/** And the one it casts Jonas in, at the kiosk. */
const VENDOR = 'vendor';

/**
 * The counters of the travel centre, which are the same four every day.
 *
 * The furniture of the hall rather than anything a round says: which of them a number is called to
 * is spoken and never sent, so a screen offering the four it can see is a screen that cannot leak
 * the one it is not told.
 */
const COUNTERS = [1, 2, 3, 4];

/**
 * The platforms of the station, which are the same twelve every day.
 *
 * Furniture of the station too, and offered whole rather than down to what the board prints. A train
 * moved to another Gleis is called out over the speakers and printed nowhere, and the Gleis it has
 * moved to is one no departure on the board stands against — so a screen offering only the printed
 * ones is a screen a traveller who listened cannot walk on.
 */
const GLEISE = Array.from({ length: 12 }, (_, index) => index + 1);

/** The sign over that strip, which is also what the strip takes its name from. */
const GLEISE_SIGN = 'bahn-gleise-sign';

/**
 * A city, worded from the browser's own translations off the id the wire carries.
 *
 * Every one of them comes out German whatever language the app is read in: the cities are where
 * the trains go, not something the app is saying, and a board that renamed them would be a board
 * the student cannot read against the one in the station.
 */
export const city = (id, t) => t(`bahn.city.${id}`, id);

/**
 * One line of the errand, worded from the browser's own translations.
 *
 * Every kind the wire can carry is worded here, the ones the sheet adds included, so the promise
 * made before a scene and the outcome shown after the run read as the same sentence.
 *
 * A kiosk line is worded off the very phrase the shopping list in front of the shelf used, so what
 * the traveller was told to bring and what they are scored on read alike.
 */
export function objectiveLabel(objective, t) {
  return t(`bahn.objective.${objective.kind}`, {
    city: objective.destination && city(objective.destination, t),
    journey: objective.journey && t(`bahn.journey.${objective.journey}`),
    travelClass: objective.travelClass && t(`bahn.class.${objective.travelClass}`),
    time: objective.arriveBefore,
    item: objective.bring && t(`bahn.kiosk.line.${objective.bring}`),
  });
}

/**
 * What the traveller was sent for, and the money they were sent with — without the money where the
 * scene says its own, which is the scene's to say, and down to where they are going where the rest
 * of the errand bears on nothing.
 *
 * The line left standing is taken by kind rather than by rank: what order they come over in is the
 * server's.
 */
function Errand({ round, t, money = true, whole = true }) {
  const lines = whole
    ? round.objectives
    : round.objectives.filter((objective) => objective.kind === 'DESTINATION');

  return (
    <section className="bahn-errand">
      <h2>{t('bahn.errand.heading')}</h2>
      <ul aria-label={t('bahn.errand.heading')}>
        {lines.map((objective, index) => (
          <li key={index}>{objectiveLabel(objective, t)}</li>
        ))}
      </ul>

      {money && (
        <>
          <h2>{t('bahn.wallet')}</h2>
          <p className="bahn-money">{euros(round.walletEuro)}</p>
        </>
      )}
    </section>
  );
}

/**
 * The ticket in the traveller's hand, as it is printed.
 *
 * No platform on it, because a German ticket does not print one: which Gleis a train leaves from
 * is on the board, and reading it off there is a scene of its own.
 *
 * The train is named the way the board prints it and the platform calls it in — the kind and the
 * number, `ICE 574` — because finding that same name on the board is what the traveller is holding
 * it for.
 */
function Ticket({ round, t }) {
  const { ticket } = round;
  return (
    <section className="bahn-ticket" aria-label={t('bahn.ticket.heading')}>
      <h2>{t('bahn.ticket.heading')}</h2>
      {!ticket && <p className="bahn-none">{t('bahn.ticket.none')}</p>}
      {ticket && (
        <dl>
          <dt>{t('bahn.ticket.to')}</dt>
          <dd>{city(ticket.destination, t)}</dd>
          <dt>{t('bahn.ticket.train')}</dt>
          <dd>
            {ticket.train} {ticket.number}
          </dd>
          <dt>{t('bahn.ticket.departs')}</dt>
          <dd>{ticket.departsAt}</dd>
          <dt>{t('bahn.ticket.journey')}</dt>
          <dd>
            {t(`bahn.journey.${ticket.journey}`)} · {t(`bahn.class.${ticket.travelClass}`)}
          </dd>
          <dt>{t('bahn.ticket.paid')}</dt>
          <dd>{euros(ticket.paidEuro)}</dd>
        </dl>
      )}
    </section>
  );
}

/**
 * The one line of the ticket the kiosk draws: the hour the train goes, and nothing else off it.
 *
 * The clock over the scene says what time it is now; what it is running down to is printed on the
 * ticket and on the board, and neither is on the screen at the shelf. Browsing costs the run
 * minutes and the run is scored on catching the train, so the deadline stands where the shopping
 * that races it happens.
 *
 * A run no departure answered is handed no ticket, and then this says nothing at all: an empty
 * label under the basket is a deadline the traveller cannot read.
 */
function Departure({ round, t }) {
  const { ticket } = round;
  if (!ticket) return null;
  return <p className="bahn-departs">{t('bahn.kiosk.departs', { time: ticket.departsAt })}</p>;
}

/**
 * What the traveller carries with them: what they were sent for, and how far they have got with it.
 *
 * The column of the counter, the board and the platform.
 */
function Carried({ round, t }) {
  return (
    <>
      <Errand round={round} t={t} />
      <Ticket round={round} t={t} />
    </>
  );
}

/**
 * What stands there instead at the kiosk: not what the traveller holds, but what they are there to
 * buy — where they are going, then what they were told to bring and how far they have got with it,
 * on one card.
 *
 * The list and the basket are the traveller's own, so they are carried here rather than printed on
 * the shelf. What is on the shelf and what it costs is the kiosk's.
 *
 * No ticket, save the hour it leaves at, which the shopping is racing. And no wallet on the errand:
 * the money of this scene is what is left to spend, said once under the basket that comes out of it.
 *
 * The errand itself is down to where they are going for the same reason. What the ticket had to
 * be — the journey, the hour it arrives, the fare it cost — is a condition on one already bought,
 * and the sheet reads all of it back at the end.
 */
function Shopping({ round, t }) {
  return (
    <>
      <Errand round={round} t={t} money={false} whole={false} />
      <section className="bahn-bringing">
        <ShoppingList kiosk={round.kiosk} t={t} />
        <Basket kiosk={round.kiosk} t={t} />
        <Departure round={round} t={t} />
      </section>
    </>
  );
}

/**
 * The column beside the scene, and — where the scene has one — the way on out of it, standing under
 * the column.
 *
 * Under it because that is what the way on is waiting on for most of the run: the column says how
 * far the traveller has got, and the button under it is what opens once the scene is done. A scene
 * with no way on out of it passes no [onFinish] and gets none, rather than a button that could
 * never be pressed.
 *
 * Which cards stand in the column is the scene's own, so the frame is told rather than knowing:
 * three of them carry what the traveller holds, the kiosk what they are there to buy.
 */
function Carrying({ round, t, mayFinish, onFinish, Column = Carried }) {
  return (
    <div className="bahn-carrying">
      <Column round={round} t={t} />
      {onFinish && <WayOn mayFinish={mayFinish} onFinish={onFinish} />}
    </div>
  );
}

/**
 * The interstitial that opens a scene: the place ahead, what is done there, and the errand the
 * whole run is for.
 *
 * The errand is the run's rather than the scene's, because it spans the scenario, and what each
 * scene draws of it is the scene's own. It gates nothing — reading it is the whole of what happens
 * here — and it is worded from ids, so an A2 learner reads what they were sent to do in a language
 * they already have.
 *
 * The column beside it is the one the scene itself stands under, so the walk up to a place changes
 * nothing but who is standing there.
 */
const openedBy = (heading, intro, Column) =>
  function Briefing({ round }) {
    const { t } = useTranslation();
    return (
      <>
        <h1>{t(heading)}</h1>
        <p>{t(intro)}</p>
        <Carrying round={round} t={t} Column={Column} />
      </>
    );
  };

/**
 * Somebody to speak to, standing under the sign of the place they stand in.
 *
 * The conversation happens where the scene is rather than on a screen of its own — a traveller sent
 * elsewhere to speak can no longer see what they came for — and it opens on the way in, because the
 * click that walked them up is the gesture the microphone prompt hangs off.
 *
 * The station has two of these and they differ in nothing but who is behind the counter, so the
 * part they share is written once and told which role it is opened against.
 *
 * A frame arriving here is always one a character is still speaking over, so that is stamped on it
 * here rather than by each scene: no caller could legitimately say otherwise, and one that said the
 * wrong thing would compile and show up as a mis-ducked microphone.
 */
function Spoken({ runId, role, sign, onFrame, onSpeaking }) {
  const { t } = useTranslation();
  // Whether the teacher overlay is above the counter. Memory-only, like the rest of the screen's
  // own business: no record of it survives a reload.
  const [teacherOpen, setTeacherOpen] = useState(false);

  // Closed over the run rather than the round: a ticket bought must not remint the ticket to talk.
  const speakToThem = useCallback(() => startSceneVoiceSession(runId, role), [runId, role]);

  // The teacher door of the same run. The role says whose conversation the teacher reads and
  // where the exchange is recorded — never who the teacher is.
  const askTheTeacher = useCallback(() => startSceneTeacherSession(runId, role), [runId, role]);

  /** Down the socket the conversation already holds, with the character still speaking. */
  const heard = useCallback((frame) => onFrame(frame, true), [onFrame]);

  return (
    <>
      <div className="bahn-counter">
        <p className="bahn-sign">{t(sign)}</p>
        {/* Hidden, never unmounted, while the teacher is above the counter: unmounting stops the
            session, and they are meant to be exactly where the traveller left them. */}
        <div hidden={teacherOpen}>
          <Voice
            openSession={speakToThem}
            onScene={heard}
            onSpeaking={onSpeaking}
            openOnMount
            hidden={teacherOpen}
          />
          <button
            type="button"
            className="bahn-teacher-ask"
            onClick={() => setTeacherOpen(true)}
          >
            {t('chat.askTeacher')}
          </button>
        </div>
      </div>
      {/* One conversation on the screen, two on the wire: the teacher takes the microphone and the
          room, the character keeps their session and their memory of the visit. */}
      {teacherOpen && (
        <div className="bahn-teacher" role="dialog" aria-label={t('chat.askTeacher')}>
          <Voice
            openSession={askTheTeacher}
            greeted={false}
            idleCaption={t('bahn.teacher.listening')}
            onClose={() => setTeacherOpen(false)}
            openOnMount
          />
        </div>
      )}
    </>
  );
}

/**
 * Warten: the press that stands the student through a wait nobody should sit out.
 *
 * The same one in both scenes that have something coming, because it is the same thing: the station
 * clock goes on to whatever is next and the student's own does not move at all. What that is worth
 * waiting for is the scene's, which is why [label] comes from the scene rather than from here.
 */
function Wait({ label, onAct }) {
  return (
    <button type="button" className="bahn-wait" onClick={() => onAct({ waits: true })}>
      {label}
    </button>
  );
}

/**
 * Das Reisezentrum: a numbered slip, the board over the hall showing the number being called, and
 * the four counters it might be called to.
 *
 * Nobody stands here — there is nothing to ask anybody — so no session is opened and no character
 * is stood in for. What is said is said over the whole run, by the station itself: the number, and
 * the counter to go to. The counter is nowhere on this screen because it is nowhere in what the
 * screen is handed, so listening is the only way to know which of the four to press.
 */
function TravelCentre({ round, mayFinish, onFinish, onAct }) {
  const { t } = useTranslation();
  const hall = round.travelCentre;
  // The two things the student presses here. What either does to the run is the run's, so they are
  // named here and carried out by the player.
  const walkUpTo = (counter) => onAct({ travelCentre: { counter } });

  return (
    <main className="bahn-scene">
      <div className="bahn-travel-centre">
        <p className="bahn-sign">{t('bahn.travelCentre.sign')}</p>
        <p className="bahn-slip">
          {t('bahn.travelCentre.slip', { number: hall.waitingNumber })}
        </p>
        <p className="bahn-calling">
          {hall.calling
            ? t('bahn.travelCentre.calling', { number: hall.calling })
            : t('bahn.travelCentre.quiet')}
        </p>
        <Wait label={t('bahn.travelCentre.wait')} onAct={onAct} />
        <ul className="bahn-counters" aria-label={t('bahn.travelCentre.counters')}>
          {COUNTERS.map((counter) => (
            <li key={counter}>
              <button
                type="button"
                className="bahn-counter-door"
                onClick={() => walkUpTo(counter)}
                aria-label={t('bahn.travelCentre.walkUpTo', { counter })}
              >
                {counter}
              </button>
            </li>
          ))}
        </ul>
        {hall.reached && <p className="bahn-found">{t('bahn.travelCentre.reached')}</p>}
        {/* Why they were sent back, and never where they should have gone: the counter is not on
            the screen after a rebuff any more than it was before one. */}
        {hall.sentBack && (
          <p className="bahn-wrong">{t(`bahn.travelCentre.sentBack.${hall.sentBack}`)}</p>
        )}
      </div>
      <Carrying round={round} t={t} mayFinish={mayFinish} onFinish={onFinish} />
    </main>
  );
}

/**
 * Something brought up over the scene: a card in the middle of a dimmed hall, with the way to put it
 * away again at the foot of it.
 *
 * Whoever the traveller was speaking to is left standing underneath and still in their session, so
 * what comes up here is a glance taken and given back rather than somewhere else to be. Down is
 * hidden rather than unmounted, for the same reason the teacher hides the counter instead of
 * pulling it down: what is in it — a session, a half-filled form — is meant to be exactly where it
 * was left.
 */
function Popped({ up, label, card, onClose, children }) {
  const { t } = useTranslation();

  return (
    <div className="bahn-over" hidden={!up}>
      <div className={`bahn-over-box ${card}`} role="dialog" aria-label={label}>
        {children}
        <button type="button" className="bahn-over-close" onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
    </div>
  );
}

/**
 * The panel that stands in for the character until there is one, behind the same press as
 * everything else that comes up over a scene.
 *
 * A developer's console standing open beside the counter is a scene nobody can look at without it:
 * it is wider than what it stands next to and it is not what the student is here to read. Whether
 * it is offered at all is the deployment's, which the round carries.
 */
function StandIn({ runId, onFrame }) {
  const { t } = useTranslation();
  // Whether the panel is up. Memory-only, like the rest of the screen's own business.
  const [open, setOpen] = useState(false);

  return (
    <>
      <button type="button" className="bahn-look" onClick={() => setOpen(true)}>
        {t('characterPanel.heading')}
      </button>
      <Popped
        up={open}
        label={t('characterPanel.heading')}
        card="bahn-stub"
        onClose={() => setOpen(false)}
      >
        <CharacterPanel runId={runId} onFrame={onFrame} />
      </Popped>
    </>
  );
}

/**
 * Der Fahrkartenschalter: the clerk behind the counter, and the first conversation the station
 * holds. She speaks German and only German.
 */
function Counter({ round, runId, mayFinish, onFinish, onFrame, onSpeaking }) {
  const { t } = useTranslation();

  /** Back from the panel that stands in for her, with nobody speaking. */
  const acted = useCallback((frame) => onFrame(frame, false), [onFrame]);

  return (
    <main className="bahn-scene">
      <div className="bahn-place">
        <Spoken
          runId={runId}
          role={CLERK}
          sign="bahn.counter.sign"
          onFrame={onFrame}
          onSpeaking={onSpeaking}
        />
        {round.characterStub && <StandIn runId={runId} onFrame={acted} />}
      </div>
      <Carrying round={round} t={t} mayFinish={mayFinish} onFinish={onFinish} />
    </main>
  );
}

/**
 * What the traveller was told to bring for the journey: categories rather than things, so saying
 * what they want is the whole of the exchange.
 *
 * Worded from the browser's own translations off the ids the wire carries, for the reason the
 * errand's lines are.
 */
function ShoppingList({ kiosk, t }) {
  return (
    <>
      <h2>{t('bahn.kiosk.list')}</h2>
      <ul className="bahn-shopping" aria-label={t('bahn.kiosk.list')}>
        {kiosk.shoppingList.map((line) => (
          <li key={line}>{t(`bahn.kiosk.line.${line}`)}</li>
        ))}
      </ul>
    </>
  );
}

/**
 * The shelf: what the kiosk sells and what it costs, in words and nothing else.
 *
 * Everything is on it, today's shortage included, and nothing on it says which category an item
 * answers. A shelf that printed either would let the traveller match it against the list by eye
 * instead of asking for what they want and coping with being told no.
 *
 * Asked for rather than standing open, so what is in front of the traveller by default is the
 * vendor: the prices are there to be checked when a price is what is wanted, and reading them is a
 * glance taken away from the conversation and put back.
 */
function Shelf({ kiosk, t }) {
  return (
    <>
      <h2>{t('bahn.kiosk.shelf')}</h2>
      <table className="bahn-shelf" aria-label={t('bahn.kiosk.shelf')}>
        <tbody>
          {kiosk.shelf.map((item) => (
            <tr key={item.id}>
              <td>{t(`bahn.kiosk.item.${item.id}`, item.id)}</td>
              <td>{euros(item.priceEuro)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/**
 * What the traveller has asked for so far, and what they have left to pay with.
 *
 * The money here is not the wallet on the errand. That is what they set out with; the fare came out
 * of it at the counter and the basket comes out of what it left, so a traveller who bought a dearer
 * ticket than they were sent for stands at this shelf with less. Drawn because otherwise the only
 * way to find out is to ask for something and be refused.
 */
function Basket({ kiosk, t }) {
  return (
    <>
      <h2>{t('bahn.kiosk.basket')}</h2>
      <ul className="bahn-basket" aria-label={t('bahn.kiosk.basket')}>
        {kiosk.basket.length === 0 && (
          <li className="bahn-none">{t('bahn.kiosk.basketEmpty')}</li>
        )}
        {kiosk.basket.map((line) => (
          <li key={line.itemId}>
            {line.quantity} × {t(`bahn.kiosk.item.${line.itemId}`, line.itemId)}
          </li>
        ))}
      </ul>

      <h2>{t('bahn.kiosk.left')}</h2>
      <p className="bahn-money">{euros(kiosk.leftEuro)}</p>
    </>
  );
}

/**
 * Der Kiosk: the vendor behind the shelf, and the station's second conversation. He speaks German
 * and only German.
 *
 * A visit is not left by pressing anything: it is left by taking leave of the vendor, and he is the
 * one who says it is over. That is what the way on out of this scene waits for.
 *
 * The shelf is asked for and comes up over him, and he is left standing and mid-session underneath:
 * a price looked up is a glance, not somewhere else to be.
 */
function Kiosk({ round, runId, mayFinish, onFinish, onFrame, onSpeaking }) {
  const { t } = useTranslation();

  // Whether the shelf is up in front of the vendor. Memory-only, like the rest of the screen's own
  // business: a reload comes back to a kiosk with nothing over it.
  const [shelfOpen, setShelfOpen] = useState(false);

  /** Back from the panel that stands in for him, with nobody speaking. */
  const acted = useCallback((frame) => onFrame(frame, false), [onFrame]);

  return (
    <main className="bahn-scene">
      <div className="bahn-place">
        <Spoken
          runId={runId}
          role={VENDOR}
          sign="bahn.kiosk.sign"
          onFrame={onFrame}
          onSpeaking={onSpeaking}
        />
        <button type="button" className="bahn-look" onClick={() => setShelfOpen(true)}>
          {t('bahn.kiosk.look')}
        </button>
        {round.characterStub && <StandIn runId={runId} onFrame={acted} />}
      </div>
      <Popped
        up={shelfOpen}
        label={t('bahn.kiosk.shelf')}
        card="bahn-kiosk-shelf"
        onClose={() => setShelfOpen(false)}
      >
        <Shelf kiosk={round.kiosk} t={t} />
      </Popped>
      <Carrying round={round} t={t} mayFinish={mayFinish} onFinish={onFinish} Column={Shopping} />
    </main>
  );
}

/**
 * Die Anzeigetafel: every departure of the day, and the traveller's own ticket beside it.
 *
 * Nobody stands here — there is nothing to ask anybody — so no session is opened and no character
 * is stood in for. The one thing to do is press the platform the ticket's train leaves from, and a
 * wrong one is a walk there and back with the board still up to be read again.
 *
 * Which is pressed either on the row that prints it or on the strip of the station's own platforms
 * under the board, because the board is not always right about where a train leaves from and never
 * says so: what it prints is one way to a Gleis rather than the only one.
 *
 * The board is a table because that is what it is: four columns of the same four facts, which a
 * screen reader walks a row at a time. Its headings are German, because it is a German board.
 */
function Board({ round, mayFinish, onFinish, onAct }) {
  const { t } = useTranslation();
  const { board } = round;
  // The one thing the student presses in the whole station. What it does to the run is the run's,
  // so the walk is named here and carried out by the player.
  const walkTo = (platform) => onAct({ board: { platform } });

  return (
    <main className="bahn-scene">
      <div className="bahn-board">
        <p className="bahn-sign">{t('bahn.board.sign')}</p>
        <table aria-label={t('bahn.board.heading')}>
          <thead>
            <tr>
              <th scope="col">{t('bahn.board.zeit')}</th>
              <th scope="col">{t('bahn.board.zug')}</th>
              <th scope="col">{t('bahn.board.ziel')}</th>
              <th scope="col">{t('bahn.board.gleis')}</th>
            </tr>
          </thead>
          <tbody>
            {board.connections.map((connection) => (
              <tr key={connection.id}>
                <td>{connection.departsAt}</td>
                <td>
                  {connection.train} {connection.number}
                </td>
                <td>{city(connection.destination, t)}</td>
                <td>
                  <button
                    type="button"
                    className="bahn-platform"
                    onClick={() => walkTo(connection.platform)}
                    aria-label={t('bahn.board.walkTo', { platform: connection.platform })}
                  >
                    {connection.platform}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <h2 className="bahn-gleise-sign" id={GLEISE_SIGN}>
          {t('bahn.board.platforms')}
        </h2>
        <ul className="bahn-gleise" aria-labelledby={GLEISE_SIGN}>
          {GLEISE.map((platform) => (
            <li key={platform}>
              <button
                type="button"
                className="bahn-platform"
                onClick={() => walkTo(platform)}
                aria-label={t('bahn.board.walkTo', { platform })}
              >
                {platform}
              </button>
            </li>
          ))}
        </ul>
        {board.wentTo !== null && (
          <p className={board.found ? 'bahn-found' : 'bahn-wrong'}>
            {board.found
              ? t('bahn.board.found', { platform: board.wentTo })
              : t('bahn.board.wrong', { platform: board.wentTo })}
          </p>
        )}
      </div>
      <Carrying round={round} t={t} mayFinish={mayFinish} onFinish={onFinish} />
    </main>
  );
}

/**
 * Der Bahnsteig: the platform the board sent them to, the trains using it, and the clock running
 * down on it.
 *
 * What is standing there is whatever the server says is standing there at this reading, each train
 * showing where it is going and nothing more — which of them is theirs is the ticket beside it to
 * answer. An empty platform is a platform waited on, and says so — and the wait is pressed rather
 * than sat through, which puts the next train in front of them.
 *
 * Every train standing there is offered to be got onto, theirs and the rest alike: a screen that
 * offered only their own would be the screen answering the one question the scene asks. What comes
 * of pressing one is the run's — a conductor at the door of the wrong train, and nothing said here
 * about which that is until they have tried it.
 *
 * Nobody to talk to, and no way on. A platform is not left by pressing anything: it is left by the
 * train, whenever the clock says so, and it is the last scene of the walk in any case.
 */
function Platform({ round, mayFinish, onFinish, onAct }) {
  const { t } = useTranslation();
  const standing = round.atThePlatform ?? [];
  // The train is named to the run by the city on its sign, which is what tells one standing there
  // from another.
  const board = (destination) => onAct({ platform: { destination } });

  return (
    <main className="bahn-scene">
      <div className="bahn-platform-sign">
        <p className="bahn-gleis">{t('bahn.platform.sign', { platform: round.standingOn })}</p>
        {standing.length === 0 ? (
          <p>{t('bahn.platform.waiting')}</p>
        ) : (
          <table aria-label={t('bahn.platform.standing')}>
            <thead>
              <tr>
                <th scope="col">{t('bahn.board.zeit')}</th>
                <th scope="col">{t('bahn.board.zug')}</th>
                <th scope="col">{t('bahn.board.ziel')}</th>
                <th scope="col">{t('bahn.platform.einsteigen')}</th>
              </tr>
            </thead>
            <tbody>
              {standing.map((train) => (
                <tr key={train.destination}>
                  <td>{train.departsAt}</td>
                  <td>
                    {train.train} {train.number}
                  </td>
                  <td>{city(train.destination, t)}</td>
                  <td>
                    <button
                      type="button"
                      className="bahn-platform"
                      onClick={() => board(train.destination)}
                      aria-label={t('bahn.platform.board', {
                        city: city(train.destination, t),
                      })}
                    >
                      {t('bahn.platform.einsteigen')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Wait label={t('bahn.platform.wait')} onAct={onAct} />
        {round.aboard && (
          <p className="bahn-found">
            {t('bahn.platform.aboard', { city: city(round.aboard, t) })}
          </p>
        )}
        {round.turnedAwayFrom && (
          <p className="bahn-wrong">
            {t('bahn.platform.turnedAway', { city: city(round.turnedAwayFrom, t) })}
          </p>
        )}
      </div>
      <Carrying round={round} t={t} mayFinish={mayFinish} onFinish={onFinish} />
    </main>
  );
}

/**
 * The run is over, and what it came to is a sheet: how it ended, a checkmark per line in the order
 * the run happened, and the money.
 *
 * The sheet is the run's rather than any one scene's — it is scored on the whole walk — so all
 * three scenes end on this one, and it is read off the run rather than out of the scene the
 * traveller happened to be standing in.
 *
 * The lines are taken as the sheet lists them rather than laid against the errand a second time or
 * grouped here by what they are about: the server hands them over in the order the run walked the
 * station, opening on the counter the hall called the traveller to, answering what the interstitials
 * promised in the order they promised it, and closing on the train.
 */
function Sheet({ sheet }) {
  const { t } = useTranslation();
  return (
    <section className="bahn-result">
      <h1>{t(`bahn.result.${sheet.ending}`)}</h1>
      <ul aria-label={t('bahn.result.heading')}>
        {sheet.objectives.map((outcome, index) => (
          <li key={index}>
            <span>{objectiveLabel(outcome.objective, t)}</span>
            <span className={outcome.met ? 'bahn-met' : 'bahn-missed'}>
              {outcome.met ? t('bahn.result.met') : t('bahn.result.missed')}
            </span>
          </li>
        ))}
      </ul>

      {/* What the traveller came away from the shelf with that no line asked for. Named rather
          than scored: nothing here is held against a run, and a traveller who bought none of it
          is told nothing rather than told they bought nothing. */}
      {sheet.extras.length > 0 && (
        <p>
          {t('bahn.result.alsoBought')}{' '}
          {sheet.extras.map((id) => t(`bahn.kiosk.item.${id}`, id)).join(', ')}
        </p>
      )}

      <p>
        {t('bahn.result.spent', { spent: euros(sheet.spentEuro) })} ·{' '}
        {t('bahn.result.left', { left: euros(sheet.remainingEuro) })}
      </p>
      <p className="bahn-tally">
        {t('bahn.result.tally', { met: sheet.metCount, total: sheet.objectives.length })} ·{' '}
        {sheet.perfect ? t('bahn.result.perfect') : t('bahn.result.notQuite')}
      </p>
    </section>
  );
}

/**
 * Where a run of the Hauptbahnhof keeps what it came to: on the run, beside the scene rather than
 * inside it, and not there at all until the run is over.
 *
 * The same for all five scenes, because the sheet is scored on all five of them.
 */
const theRunsSheet = (run) => run.result?.bahn;

/**
 * The five scenes of the Hauptbahnhof as the player can host them: which scene each draws, and
 * which field of that scene is its own, how it is named while it is on the screen, the palette it
 * dresses the player in, what it makes of the round it is given, and the three faces it is looked
 * at through.
 *
 * All five read the same field, because the round spans the scenario: the number waited for in the
 * hall sends the traveller to the counter, the ticket bought there is the ticket read off the
 * board, carried past the kiosk down to the hour it leaves at, and shown on the platform. What
 * differs is how much of it each of them draws.
 *
 * None of them has a face of its own to end on. A scene here is scored nowhere but at the end of
 * the walk, so `over` — the round carrying an account the scene ends on — is false in all five,
 * and the sheet they share is the run's.
 *
 * Which is not to say they never finish. All five do, and say so under `opensTheWayOut`: the
 * traveller standing at the counter they were called to, a ticket in their hand, the traveller
 * standing where their train leaves from, a visit the vendor has closed, and the traveller on their
 * train. That is what sends the player to ask the run whether the way on may open — the two are
 * separate questions here, where in a scenario played in one scene they are the same one.
 */
export const travelCentreScreen = {
  sceneKey: HAUPTBAHNHOF_TRAVEL_CENTRE,
  payload: 'bahn',
  title: 'bahn.title',
  subtitle: 'bahn.subtitle',
  enter: 'bahn.travelCentre.enter',
  palette: 'bahn',
  /** Standing at the counter they were called to, so a reload comes back to the hall behind them. */
  begun: (round) => Boolean(round.travelCentre?.reached),
  over: () => false,
  /** And the same standing is what finishes the hall: the call answered is the whole of the scene. */
  opensTheWayOut: (round) => Boolean(round.travelCentre?.reached),
  sheet: theRunsSheet,
  Briefing: openedBy('bahn.travelCentre.heading', 'bahn.travelCentre.intro'),
  Stage: TravelCentre,
  Result: Sheet,
};

export const counterScreen = {
  /** The scene this draws, as the run names it. */
  sceneKey: HAUPTBAHNHOF_COUNTER,
  /** The field of that scene carrying its round, which is all these components are ever given. */
  payload: 'bahn',
  title: 'bahn.title',
  subtitle: 'bahn.subtitle',
  enter: 'bahn.counter.enter',
  palette: 'bahn',
  /** A ticket bought, so a reload comes back to the counter rather than to its interstitial. */
  begun: (round) => Boolean(round.ticket),
  over: () => false,
  /**
   * And the same ticket is what finishes the counter: the errand there is to be sold one, and
   * whether it was the right one is the score sheet's business. The clerk sells it, so this is a
   * round the traveller cannot reach by pressing anything — without it the run is never asked
   * again after the sale, and they are shut in with a clerk who has nothing left to sell them.
   */
  opensTheWayOut: (round) => Boolean(round.ticket),
  sheet: theRunsSheet,
  Briefing: openedBy('bahn.counter.heading', 'bahn.counter.intro'),
  Stage: Counter,
  Result: Sheet,
};

export const boardScreen = {
  sceneKey: HAUPTBAHNHOF_BOARD,
  payload: 'bahn',
  title: 'bahn.title',
  subtitle: 'bahn.subtitle',
  enter: 'bahn.board.enter',
  palette: 'bahn',
  /** A platform already walked to, right or wrong: the reading of the board is under way. */
  begun: (round) => Boolean(round.board) && round.board.wentTo !== null,
  over: () => false,
  /**
   * Standing where their own train leaves from, which is the whole of having read the board. Named
   * for the same reason the counter's is, though nobody here pushes a frame: the walk is the
   * student's own press, and the run answers that one itself.
   */
  opensTheWayOut: (round) => Boolean(round.board) && round.board.found,
  sheet: theRunsSheet,
  Briefing: openedBy('bahn.board.heading', 'bahn.board.intro'),
  Stage: Board,
  Result: Sheet,
};

export const kioskScreen = {
  sceneKey: HAUPTBAHNHOF_KIOSK,
  payload: 'bahn',
  title: 'bahn.title',
  subtitle: 'bahn.subtitle',
  enter: 'bahn.kiosk.enter',
  palette: 'bahn',
  /** A visit taken leave of, so a reload comes back to the kiosk rather than to its interstitial. */
  begun: (round) => Boolean(round.kiosk?.visitOver),
  over: () => false,
  /**
   * And the same goodbye is what finishes the kiosk: the vendor closes the visit, so this is a
   * scene the traveller cannot leave by pressing anything. Without it they are shut in with a
   * vendor who has nothing left to do for them.
   */
  opensTheWayOut: (round) => Boolean(round.kiosk?.visitOver),
  sheet: theRunsSheet,
  Briefing: openedBy('bahn.kiosk.heading', 'bahn.kiosk.intro', Shopping),
  Stage: Kiosk,
  Result: Sheet,
};

export const platformScreen = {
  sceneKey: HAUPTBAHNHOF_PLATFORM,
  payload: 'bahn',
  title: 'bahn.title',
  subtitle: 'bahn.subtitle',
  enter: 'bahn.platform.enter',
  palette: 'bahn',
  /** On their train, so a reload comes back to a traveller who is no longer on the platform. */
  begun: (round) => Boolean(round.aboard),
  over: () => false,
  /** And getting on is what finishes it: the press that follows takes the train out of the station. */
  opensTheWayOut: (round) => Boolean(round.aboard),
  sheet: theRunsSheet,
  Briefing: openedBy('bahn.platform.heading', 'bahn.platform.intro'),
  Stage: Platform,
  Result: Sheet,
};
